import { Validators } from '@angular/forms';

import * as moment from 'moment';

import {
    InArrayValidator,
    OnlyLatinValidator,
    UnderEighteensValidator,
    WhitespaceValidator,
    LatinLettersAndSpecialSymbolsAndWhiteSpaceValidator, NameValidator, EmailValidator
} from '@spa/validators';

import {MiFidCategoriesEnum, MifirTypesEnum, TradingCategoriesEnum} from '@spa/api';

import { ColorCodesEnum, ComponentTypesEnum } from '../../../app/shared/enums';

import {
    BaseControl,
    CountryAutocompleteControl,
    DatepickerControl, Helper,
    RadioGroupControl,
    SelectControl
} from '../../../app/shared/classes';

export const PersonalDetailFirstNameSelector = 'firstName';
export const PersonalDetailMiddleNameSelector = 'middleName';
export const PersonalDetailLastNameSelector = 'lastName';
export const PersonalDetailGenderSelector = 'gender';
export const PersonalDetailCountryOfBirthSelector = 'countryOfBirth';
export const PersonalDetailPlaceOfBirth = 'placeOfBirth';
export const PersonalDetailDateOfBirthSelector = 'birthDate';
export const PersonalDetailMiFidCategorySelector = 'miFidCategory';
export const PersonalDetailTradingCategorySelector = 'tradingCategory';
export const PersonalDetailReferralSelector = 'referral';
export const PersonalDetailOldClientIdSelector = 'oldClientId';
export const PersonalDetailMifirNumberSelector = 'mifirNumber';
export const PersonalDetailMifirTypeSelector = 'mifirType';
export const PersonalDetailMifirExpirationDateSelector = 'mifirExpirationDate';
export const PersonalDetailSecondaryEmailSelector = 'secondaryEmail';
export const PersonalDetailReferralMaxLengthValidation = 100;
export const PersonalDetailOldClientIdMaxLengthValidation = 128;
export const PersonalDetailMifirNumberMaxLengthValidation = 30;
export const PersonalDetailMifirNumberPatternValidation = /^[a-zA-Z0-9#]+$/;

export const PersonalDetailsControls = (): BaseControl<string | number>[] => {
    return [
        new BaseControl({
            name: PersonalDetailFirstNameSelector,
            value: '',
            placeholder: 'First Name',
            validators: [WhitespaceValidator(), NameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: PersonalDetailMiddleNameSelector,
            value: '',
            placeholder: 'Middle Name',
            validators: [NameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: PersonalDetailLastNameSelector,
            value: '',
            placeholder: 'Last Name',
            validators: [WhitespaceValidator(), NameValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new SelectControl({
            name: PersonalDetailGenderSelector,
            value: '',
            placeholder: 'Gender',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever: true
        }),
        new DatepickerControl({
            name: PersonalDetailDateOfBirthSelector,
            value: '',
            placeholder: 'Date Of Birth',
            validators: [Validators.required, UnderEighteensValidator()],
            componentType: ComponentTypesEnum.Datepicker,
            maxDate: moment().format('YYYY-MM-DD'),
            minDate: moment('1930-01-01').format('YYYY-MM-DD'),
            floatingLabelNever: true
        }),
        new CountryAutocompleteControl({
            name: PersonalDetailCountryOfBirthSelector,
            value: '',
            placeholder: 'Country Of Birth',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.CountryAutocomplete,
            countries: [],
            options: [],
            floatingLabelNever: true
        }),
        new BaseControl({
            name: PersonalDetailPlaceOfBirth,
            value: '',
            placeholder: 'Place of Birth',
            validators: [WhitespaceValidator(), OnlyLatinValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new SelectControl({
            name: PersonalDetailMiFidCategorySelector,
            value: null,
            placeholder: 'MiFID Category',
            validators: [
                Validators.required,
                InArrayValidator([
                    MiFidCategoriesEnum.Retail,
                    MiFidCategoriesEnum.Professional
                ])
            ],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getMiFidCategoryName(MiFidCategoriesEnum.Retail),
                    value: MiFidCategoriesEnum.Retail,
                },
                {
                    label: Helper.textsHelper().getMiFidCategoryName(MiFidCategoriesEnum.Professional),
                    value: MiFidCategoriesEnum.Professional,
                }
            ],
            floatingLabelNever: true
        }),
        new RadioGroupControl({
            name: PersonalDetailTradingCategorySelector,
            value: null,
            placeholder: 'Investment Firm',
            validators: [
                Validators.required,
                InArrayValidator([TradingCategoriesEnum.Retail, TradingCategoriesEnum.Group])
            ],
            componentType: ComponentTypesEnum.RadioGroup,
            options: [
                {
                    label: Helper.textsHelper().getIsInvestmentFirm(TradingCategoriesEnum.Retail),
                    value: TradingCategoriesEnum.Retail,
                    color: ColorCodesEnum.Primary,
                },
                {
                    label: Helper.textsHelper().getIsInvestmentFirm(TradingCategoriesEnum.Group),
                    value: TradingCategoriesEnum.Group,
                    color: ColorCodesEnum.Primary
                }
            ]
        }),
        new BaseControl({
            name: PersonalDetailReferralSelector,
            value: '',
            placeholder: 'Referral Code',
            validators: [LatinLettersAndSpecialSymbolsAndWhiteSpaceValidator(),
                        Validators.maxLength(PersonalDetailReferralMaxLengthValidation)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: PersonalDetailOldClientIdSelector,
            value: '',
            placeholder: 'Old Client Id',
            validators: [
                        Validators.maxLength(PersonalDetailOldClientIdMaxLengthValidation)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new BaseControl({
            name: PersonalDetailMifirNumberSelector,
            value: null,
            placeholder: 'MiFIR Number',
            validators: [Validators.pattern(PersonalDetailMifirNumberPatternValidation),
                Validators.maxLength(PersonalDetailMifirNumberMaxLengthValidation)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        }),
        new SelectControl({
            name: PersonalDetailMifirTypeSelector,
            value: null,
            placeholder: 'MiFIR Type',
            validators: [
                InArrayValidator([
                    MifirTypesEnum.NIDN,
                    MifirTypesEnum.CONCAT,
                    MifirTypesEnum.CCPT,
                ])
            ],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getMiFirTypeName(MifirTypesEnum.NIDN),
                    value: MifirTypesEnum.NIDN,
                },
                {
                    label: Helper.textsHelper().getMiFirTypeName(MifirTypesEnum.CONCAT),
                    value: MifirTypesEnum.CONCAT,
                },
                {
                    label: Helper.textsHelper().getMiFirTypeName(MifirTypesEnum.CCPT),
                    value: MifirTypesEnum.CCPT,
                }
            ],
            floatingLabelNever: true
        }),
        new DatepickerControl({
            name: PersonalDetailMifirExpirationDateSelector,
            value: null,
            placeholder: 'MiFIR Expiration Date',
            validators: [],
            componentType: ComponentTypesEnum.Datepicker,
            minDate: null,
            maxDate: null,
            floatingLabelNever: true,
            resetable: true
        }),
        new BaseControl({
            name: PersonalDetailSecondaryEmailSelector,
            value: '',
            placeholder: 'Secondary Email',
            validators: [EmailValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever: true
        })
    ];
};

